import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { CompanyDTO } from "@shaarp/common";
import { InfoIcon } from "lucide-react";
import { AiOutlineSafetyCertificate } from "react-icons/ai";
import { BiSolidBadgeCheck } from "react-icons/bi";
import { Button } from "../ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

export default function AlertCompanyDatasourcesCoverage({
  company,
  triggerDialogSourcesEdit = () => {},
  className = "",
}: {
  company: CompanyDTO;
  triggerDialogSourcesEdit?: () => void;
  className?: string;
}) {
  const { t } = useTranslation();

  const scoring = company.dataSources?.scoring || null;

  if (!scoring) {
    return null;
  }

  const getLabel = (): string => {
    if (company.isVerified)
      return t("company.scoring.compact.verified" as Parameters<typeof t>[0]);
    if (scoring === 100)
      return t("company.scoring.compact.complete" as Parameters<typeof t>[0]);
    if (scoring >= 40)
      return t("company.scoring.compact.partial" as Parameters<typeof t>[0]);
    return t("company.scoring.compact.incomplete" as Parameters<typeof t>[0]);
  };

  const getTooltipContent = (): string => {
    if (company.isVerified) return t("company.scoring.verified.description");
    return t("company.scoring.great.description");
  };

  const Icon = company.isVerified
    ? BiSolidBadgeCheck
    : scoring === 100
      ? AiOutlineSafetyCertificate
      : InfoIcon;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "h-10 px-3 gap-2",
            company.isVerified && "border-emerald-500 text-emerald-600",
            !company.isVerified &&
              scoring < 40 &&
              "border-orange-400 text-orange-500",
            className,
          )}
          onClick={() => triggerDialogSourcesEdit()}
        >
          <Icon className="w-4 h-4 shrink-0" />
          <span className="text-sm">{getLabel()}</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <div className="text-center">
          <div className="font-medium">{getLabel()}</div>
          <div
            className="text-xs text-muted-foreground"
            dangerouslySetInnerHTML={{ __html: getTooltipContent() }}
          />
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
